import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Save, Send, RefreshCcw } from "lucide-react";

/**
 * EDITOR simulation. Used by message construction & communication conduct.
 * Events: text_entered, draft_revised, unprofessional_tone, final_decision.
 */
export default function EditorSim({ scenario, recorder, onFinalize, busy }) {
  const [text, setText] = useState("");
  const [revisions, setRevisions] = useState(0);
  const [saved, setSaved] = useState(false);

  const expected = (scenario.expected_keywords || []).map((s) => s.toLowerCase());
  const bad = (scenario.bad_keywords || []).map((s) => s.toLowerCase());
  const minWords = scenario.min_words || 0;

  const wordCount = text.trim().split(/\s+/).filter(Boolean).length;

  const onChange = (val) => {
    setText(val);
    setSaved(false);
  };
  const save = () => {
    setSaved(true);
    setRevisions((r) => r + 1);
    if (revisions === 0) recorder.log("text_entered", `${wordCount}w`);
    else recorder.log("draft_revised", `${wordCount}w`);
    if (bad.some((k) => text.toLowerCase().includes(k))) recorder.log("unprofessional_tone");
  };

  const finalize = () => {
    const wordsOk = wordCount >= minWords;
    const expectedOk = expected.length === 0 || expected.some((k) => text.toLowerCase().includes(k));
    const toneOk = !bad.some((k) => text.toLowerCase().includes(k));
    let decision;
    if (scenario.bad_keywords && scenario.bad_keywords.length > 0 && !toneOk) {
      recorder.log("unprofessional_tone");
      decision = "unprofessional";
    } else {
      decision = wordsOk && expectedOk && toneOk
        ? (scenario.bad_keywords && scenario.bad_keywords.length > 0 ? "sent_professional_reply" : "sent_clear_message")
        : "draft_incomplete";
    }
    onFinalize(decision, { text, revisions, wordCount });
  };

  return (
    <div className="p-5 grid gap-4" data-testid="editor-sim">
      {scenario.context && (
        <div className="paper p-3 surface border">
          <div className="label-mono">Context</div>
          <p className="text-sm mt-1">{scenario.context}</p>
        </div>
      )}
      <Textarea
        value={text}
        onChange={(e) => onChange(e.target.value)}
        rows={9}
        className="rounded-none border-[var(--iddl-rule)] focus-visible:ring-0 focus-visible:border-[var(--iddl-ink)]"
        placeholder="Write your message here. Revise before sending."
        data-testid="editor-textarea"
      />
      <div className="flex flex-wrap items-center gap-2">
        <span className="label-mono">{wordCount} words · revisions: {revisions}</span>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" className="rounded-none" onClick={save} data-testid="btn-editor-save">
            <Save size={12} className="mr-1" /> {saved ? "Saved" : "Save draft"}
          </Button>
          <Button size="sm" variant="outline" className="rounded-none" onClick={() => { setText(""); setRevisions((r) => r + 1); recorder.log("draft_revised", "cleared"); }} data-testid="btn-editor-clear">
            <RefreshCcw size={12} className="mr-1" /> Clear &amp; rewrite
          </Button>
          <Button size="sm" className="btn-ink rounded-none" onClick={finalize} disabled={busy} data-testid="btn-finalize-editor">
            <Send size={12} className="mr-1" /> Send / Finalise
          </Button>
        </div>
      </div>
    </div>
  );
}
